'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { getChatCompletion } from '@/lib/ai/chatCompletion';

const DOMAINS = [
  'Food Security', 'Justice & Reentry', 'Ecology', 'Housing', 'Education',
  'Health', 'Community Restoration', 'Mental Health', 'Youth', 'Elderly Care',
  'Disability', 'Masjid & Islamic Center', 'Economic Empowerment', 'Other',
];
const CITIES = [
  'Chicago', 'Atlanta', 'Portland', 'Detroit', 'New York',
  'Los Angeles', 'Houston', 'Philadelphia', 'Other',
];
const ROLES = [
  'community-caretaker', 'activist', 'academic', 'inner-seeker',
  'local-resource', 'formerly-incarcerated', 'youth', 'other',
];

// Names of Allah mapped to caretaking domains
const DOMAIN_NAMES: Record<string, { name: string; ar: string; meaning: string; ayat: string; ref: string }> = {
  'Food Security': { name: 'Ar-Razzaq', ar: 'الرَّزَّاق', meaning: 'The Ever Provider', ayat: 'And in the heaven is your provision and whatever you are promised.', ref: 'Quran 51:22' },
  'Justice & Reentry': { name: 'Al-Adl', ar: 'الْعَدْل', meaning: 'The Just', ayat: 'Indeed, Allah commands justice, good conduct, and giving to relatives.', ref: 'Quran 16:90' },
  'Ecology': { name: 'Al-Khaliq', ar: 'الْخَالِق', meaning: 'The Creator', ayat: 'And in the earth are signs for those who are certain.', ref: 'Quran 51:20' },
  'Housing': { name: 'As-Salam', ar: 'السَّلَام', meaning: 'The Source of Peace', ayat: 'Allah has made for you from your homes a place of rest.', ref: 'Quran 16:80' },
  'Education': { name: 'Al-Alim', ar: 'الْعَلِيم', meaning: 'The All-Knowing', ayat: 'Read in the name of your Lord who created.', ref: 'Quran 96:1' },
  'Health': { name: 'Ash-Shafi', ar: 'الشَّافِي', meaning: 'The Healer', ayat: 'And when I am ill, it is He who cures me.', ref: 'Quran 26:80' },
  'Community Restoration': { name: 'Al-Jabbar', ar: 'الْجَبَّار', meaning: 'The Restorer', ayat: 'And He is the Compeller, the Restorer.', ref: 'Quran 59:23' },
  'Mental Health': { name: 'Al-Latif', ar: 'اللَّطِيف', meaning: 'The Subtle, The Gentle', ayat: 'Allah is Subtle with His servants.', ref: 'Quran 42:19' },
  'Youth': { name: 'Al-Wali', ar: 'الْوَلِيّ', meaning: 'The Protecting Friend', ayat: 'Allah is the ally of those who believe.', ref: 'Quran 2:257' },
  'Elderly Care': { name: 'Ar-Rahman', ar: 'الرَّحْمَن', meaning: 'The Most Compassionate', ayat: 'Your Lord has decreed that you not worship except Him, and to parents, good treatment.', ref: 'Quran 17:23' },
  'Disability': { name: 'Al-Qadir', ar: 'الْقَادِر', meaning: 'The All-Powerful', ayat: 'We have certainly honored the children of Adam.', ref: 'Quran 17:70' },
  'Masjid & Islamic Center': { name: 'Al-Quddus', ar: 'الْقُدُّوس', meaning: 'The Most Holy', ayat: 'The mosques of Allah are only to be maintained by those who believe in Allah.', ref: 'Quran 9:18' },
  'Economic Empowerment': { name: 'Al-Mughni', ar: 'الْمُغْنِي', meaning: 'The Enricher', ayat: 'And Allah has preferred some of you over others in provision.', ref: 'Quran 16:71' },
  'Other': { name: 'Al-Wasi', ar: 'الْوَاسِع', meaning: 'The All-Encompassing', ayat: 'And to Allah belongs the east and the west. Wherever you turn, there is the Face of Allah.', ref: 'Quran 2:115' },
};

interface ResourceRequestData {
  resource_name: string;
  organization: string;
  description: string;
  city: string;
  ward: string;
  domain: string;
  website: string;
  contact_email: string;
  submitter_role: string;
  connection_to_mission: string;
}

interface VerificationResult {
  verified_name?: string;
  verified_description?: string;
  verified_address?: string;
  verified_phone?: string;
  verified_hours?: string;
  verified_services?: string[];
  verification_confidence: 'high' | 'medium' | 'low' | 'unverified';
  verification_notes?: string;
  allah_name?: string;
  allah_name_ar?: string;
  quran_ayat?: string;
  quran_ref?: string;
  caretaking_connection?: string;
}

const EMPTY: ResourceRequestData = {
  resource_name: '',
  organization: '',
  description: '',
  city: 'Chicago',
  ward: '',
  domain: 'Food Security',
  website: '',
  contact_email: '',
  submitter_role: 'community-caretaker',
  connection_to_mission: '',
};

export default function ResourceRequestForm() {
  const [form, setForm] = useState<ResourceRequestData>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState<VerificationResult | null>(null);
  const [verificationStep, setVerificationStep] = useState('');
  const [step, setStep] = useState<'form' | 'verify' | 'confirm'>('form');
  const supabase = createClient();

  const domainName = DOMAIN_NAMES[form.domain] || DOMAIN_NAMES['Other'];

  // Auto-verify when website is provided and form is ready
  async function runVerification() {
    if (!form.resource_name.trim() || !form.description.trim()) return;
    setVerifying(true);
    setVerificationStep('Analyzing resource information...');

    try {
      const domainInfo = DOMAIN_NAMES[form.domain] || DOMAIN_NAMES['Other'];
      const prompt = `You are a verification assistant for a Muslim community resource guide called Kulli Haal. 
A caretaker has submitted a resource for review. Your job is to:
1. Verify and enrich the resource information based on what you know
2. Suggest the most relevant Name/Attribute of Allah for this resource's caretaking domain
3. Suggest a relevant Quranic ayat
4. Write a brief caretaking connection statement

Resource submitted:
- Name: ${form.resource_name}
- Organization: ${form.organization || 'N/A'}
- City: ${form.city}
- Domain: ${form.domain}
- Website: ${form.website || 'not provided'}
- Description: ${form.description}
- Connection to mission: ${form.connection_to_mission || 'not provided'}

Respond ONLY with a valid JSON object in this exact format:
{
  "verified_name": "confirmed or corrected resource name",
  "verified_description": "enriched 2-3 sentence description of what this resource does",
  "verified_address": "address if known, or null",
  "verified_phone": "phone if known, or null",
  "verified_hours": "hours if known, or null",
  "verified_services": ["service 1", "service 2", "service 3"],
  "verification_confidence": "high|medium|low",
  "verification_notes": "brief note on what was verified or could not be confirmed",
  "allah_name": "${domainInfo.name}",
  "allah_name_ar": "${domainInfo.ar}",
  "quran_ayat": "relevant ayat text",
  "quran_ref": "Quran X:Y",
  "caretaking_connection": "1-2 sentences connecting this resource to the Rights of Creation and Amanah of caretaking"
}`;

      setVerificationStep('Consulting knowledge base...');

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-haiku-4-5-20251001',
        [{ role: 'user', content: prompt }],
        { temperature: 0.3, max_tokens: 800 }
      );

      setVerificationStep('Processing results...');

      const content = response?.choices?.[0]?.message?.content || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as VerificationResult;
        setVerification(parsed);
        setStep('verify');
      } else {
        // Fallback with domain defaults
        setVerification({
          verified_name: form.resource_name,
          verified_description: form.description,
          verification_confidence: 'low',
          verification_notes: 'Could not auto-verify. Manual review will be conducted.',
          allah_name: domainInfo.name,
          allah_name_ar: domainInfo.ar,
          quran_ayat: domainInfo.ayat,
          quran_ref: domainInfo.ref,
          caretaking_connection: `This resource serves the ${form.domain} domain, embodying the Amanah of caretaking in the community.`,
        });
        setStep('verify');
      }
    } catch {
      // Fallback gracefully
      const domainInfo = DOMAIN_NAMES[form.domain] || DOMAIN_NAMES['Other'];
      setVerification({
        verified_name: form.resource_name,
        verified_description: form.description,
        verification_confidence: 'low',
        verification_notes: 'Verification service unavailable. Will be reviewed manually.',
        allah_name: domainInfo.name,
        allah_name_ar: domainInfo.ar,
        quran_ayat: domainInfo.ayat,
        quran_ref: domainInfo.ref,
        caretaking_connection: `This resource serves the ${form.domain} domain, embodying the Amanah of caretaking in the community.`,
      });
      setStep('verify');
    }
    setVerifying(false);
    setVerificationStep('');
  }

  async function handleSubmit() {
    if (!form.resource_name.trim() || !form.description.trim()) {
      setError('Resource name and description are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const domainInfo = DOMAIN_NAMES[form.domain] || DOMAIN_NAMES['Other'];
      await supabase.from('resource_requests').insert({
        resource_name: form.resource_name.trim(),
        organization: form.organization.trim(),
        description: form.description.trim(),
        city: form.city,
        ward: form.ward.trim(),
        domain: form.domain,
        website: form.website.trim(),
        contact_email: form.contact_email.trim(),
        submitter_role: form.submitter_role,
        connection_to_mission: form.connection_to_mission.trim(),
        user_id: user?.id || null,
        status: 'pending',
        submitted_at: new Date().toISOString(),
        // Verification data
        verified_name: verification?.verified_name || form.resource_name,
        verified_description: verification?.verified_description || form.description,
        verified_address: verification?.verified_address || null,
        verified_phone: verification?.verified_phone || null,
        verified_hours: verification?.verified_hours || null,
        verified_services: verification?.verified_services || [],
        verification_confidence: verification?.verification_confidence || 'unverified',
        verification_notes: verification?.verification_notes || null,
        verified_at: verification ? new Date().toISOString() : null,
        // MRG integration
        allah_name: verification?.allah_name || domainInfo.name,
        allah_name_ar: verification?.allah_name_ar || domainInfo.ar,
        quran_ayat: verification?.quran_ayat || domainInfo.ayat,
        quran_ref: verification?.quran_ref || domainInfo.ref,
        caretaking_connection: verification?.caretaking_connection || form.connection_to_mission,
      });
      setSubmitted(true);
      setForm(EMPTY);
      setVerification(null);
      setStep('form');
    } catch {
      // Store locally if Supabase fails
      const queue = JSON.parse(localStorage.getItem('haal_resource_requests') || '[]');
      queue.push({ ...form, verification, submitted_at: new Date().toISOString() });
      localStorage.setItem('haal_resource_requests', JSON.stringify(queue));
      // Register background sync
      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        navigator.serviceWorker.ready.then(reg => reg.sync.register('sync-resource-requests').catch(() => {}));
      }
      setSubmitted(true);
    }
    setSubmitting(false);
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
    padding: '8px 12px', color: '#F5F0E8', fontSize: 12,
    outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em',
    textTransform: 'uppercase', display: 'block', marginBottom: 4, fontFamily: 'monospace',
  };

  const confidenceColor = {
    high: '#2d7a47',
    medium: '#c9a84c',
    low: 'rgba(212,197,169,0.5)',
    unverified: 'rgba(212,197,169,0.3)',
  };

  return (
    <section
      id="resource-request"
      style={{ background: '#060d12', padding: '64px 24px', borderTop: '1px solid rgba(45,122,71,0.15)' }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 8 }}>
            ⬡ CARETAKER CONTRIBUTION · إسهام المتكفل
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(14px,2.5vw,20px)', letterSpacing: '.15em', textTransform: 'uppercase', color: '#F5F0E8', marginBottom: 10 }}>
            Submit a Resource for the Guide
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.8, maxWidth: 560, margin: '0 auto 8px' }}>
            Know a resource, organization, or initiative that belongs in this guide? Submit it for review. Every resource is verified for alignment with the Rights of Creation and enriched with the relevant Name of Allah and Quranic Ayat before being added to the guide.
          </p>
          <p style={{ color: 'rgba(45,122,71,0.5)', fontSize: 11, fontFamily: 'monospace', marginBottom: 20 }}>
            ⬡ AI-assisted verification · Manual review · Added to city/ward guide
          </p>

          {/* Domain Name preview */}
          {!showForm && !submitted && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 8 }}>
                {Object.entries(DOMAIN_NAMES).slice(0, 6).map(([domain, info]) => (
                  <span key={domain} style={{ fontSize: 9, fontFamily: 'monospace', padding: '3px 8px', borderRadius: 3, background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', color: 'rgba(212,197,169,0.5)' }}>
                    {domain}
                  </span>
                ))}
                <span style={{ fontSize: 9, fontFamily: 'monospace', padding: '3px 8px', borderRadius: 3, color: 'rgba(212,197,169,0.3)' }}>+ more</span>
              </div>
              <button
                onClick={() => setShowForm(true)}
                style={{ fontSize: 10, fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', padding: '12px 28px', borderRadius: 5, cursor: 'pointer', background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.35)', color: '#2d7a47', transition: 'all 0.2s' }}
              >
                ⊕ Submit a Resource
              </button>
            </div>
          )}
        </div>

        {/* Success state */}
        {submitted && (
          <div style={{ textAlign: 'center', padding: '40px 24px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 12 }}>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 32, color: '#2d7a47', marginBottom: 8 }}>جَزَاكَ اللَّهُ خَيْرًا</p>
            <h3 style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#F5F0E8', marginBottom: 8 }}>JazakAllahu Khayran</h3>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7, marginBottom: 8, maxWidth: 480, margin: '0 auto 16px' }}>
              Your resource submission has been received and verified. Our team will review it for alignment with the Rights of Creation framework and add it to the guide for the relevant city and caretaking domain, inshallah.
            </p>
            {verification && (
              <div style={{ margin: '16px auto', maxWidth: 400, padding: '12px 16px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 8 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 6 }}>Assigned Name of Allah</p>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl', marginBottom: 2 }}>{verification.allah_name_ar}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#c9a84c' }}>{verification.allah_name}</p>
              </div>
            )}
            <button onClick={() => { setSubmitted(false); setShowForm(false); setStep('form'); }} style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase', padding: '8px 18px', borderRadius: 4, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(45,122,71,0.3)', color: '#2d7a47', marginTop: 8 }}>
              Submit Another Resource
            </button>
          </div>
        )}

        {/* STEP 1: Form */}
        {showForm && !submitted && step === 'form' && (
          <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 12, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <p style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)' }}>
                Step 1 of 2 · Resource Information
              </p>
              <div style={{ display: 'flex', gap: 4 }}>
                <div style={{ width: 24, height: 3, borderRadius: 2, background: '#2d7a47' }} />
                <div style={{ width: 24, height: 3, borderRadius: 2, background: 'rgba(45,122,71,0.2)' }} />
              </div>
            </div>

            {error && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: 'rgba(180,80,80,0.08)', border: '1px solid rgba(180,80,80,0.2)', borderRadius: 5, color: '#c05050', fontSize: 11, fontFamily: 'monospace' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Resource / Organization Name *</label>
                <input type="text" value={form.resource_name} onChange={e => setForm(f => ({ ...f, resource_name: e.target.value }))} placeholder="e.g. South Side Community Food Pantry" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Parent Organization (if different)</label>
                <input type="text" value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))} placeholder="e.g. Masjid Al-Taqwah" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>City</label>
                <select value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} style={inputStyle}>
                  {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Ward / Neighborhood (optional)</label>
                <input type="text" value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))} placeholder="e.g. Ward 20, Bronzeville" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Caretaking Domain</label>
                <select value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} style={inputStyle}>
                  {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Description *</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the resource — what it does, who it serves, and how it relates to caretaking..." rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Connection to Islah / Khidmah / Rights of Creation</label>
                <textarea value={form.connection_to_mission} onChange={e => setForm(f => ({ ...f, connection_to_mission: e.target.value }))} placeholder="How does this resource embody the principles of Islah, Khidmah, or the Rights of Creation?" rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
              </div>
              <div>
                <label style={labelStyle}>Website / Link (optional)</label>
                <input type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Contact Email (optional)</label>
                <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="contact@organization.org" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Your Role</label>
                <select value={form.submitter_role} onChange={e => setForm(f => ({ ...f, submitter_role: e.target.value }))} style={inputStyle}>
                  {ROLES.map(r => <option key={r} value={r}>{r.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>)}
                </select>
              </div>
            </div>

            {/* Domain Name preview */}
            <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl', marginBottom: 2 }}>{domainName.ar}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.08em' }}>{domainName.name}</p>
              </div>
              <div>
                <p style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 3 }}>Name of Allah · {form.domain}</p>
                <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', lineHeight: 1.5 }}>"{domainName.ayat}" — {domainName.ref}</p>
              </div>
            </div>

            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 5 }}>
              <p style={{ fontSize: 10, color: 'rgba(45,122,71,0.6)', fontFamily: 'monospace', lineHeight: 1.5 }}>
                ⬡ Next: Your submission will be verified using AI to enrich the resource information, confirm alignment with the Rights of Creation, and assign the relevant Name of Allah and Quranic Ayat before review.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: 5, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(212,197,169,0.5)' }}>
                Cancel
              </button>
              <button
                onClick={runVerification}
                disabled={!form.resource_name.trim() || !form.description.trim() || verifying}
                style={{
                  fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
                  padding: '8px 20px', borderRadius: 5,
                  cursor: form.resource_name.trim() && form.description.trim() && !verifying ? 'pointer' : 'not-allowed',
                  background: form.resource_name.trim() && form.description.trim() ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${form.resource_name.trim() && form.description.trim() ? 'rgba(45,122,71,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  color: form.resource_name.trim() && form.description.trim() ? '#2d7a47' : 'rgba(212,197,169,0.3)',
                  minWidth: 140,
                }}
              >
                {verifying ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', border: '1px solid #2d7a47', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                    {verificationStep || 'Verifying...'}
                  </span>
                ) : '⬡ Verify & Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Verification Review */}
        {showForm && !submitted && step === 'verify' && verification && (
          <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 12, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <p style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)' }}>
                Step 2 of 2 · Verification Review
              </p>
              <div style={{ display: 'flex', gap: 4 }}>
                <div style={{ width: 24, height: 3, borderRadius: 2, background: '#2d7a47' }} />
                <div style={{ width: 24, height: 3, borderRadius: 2, background: '#2d7a47' }} />
              </div>
            </div>

            {/* Confidence badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '10px 14px', background: 'rgba(6,13,18,0.6)', border: `1px solid ${confidenceColor[verification.verification_confidence]}30`, borderRadius: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: confidenceColor[verification.verification_confidence], flexShrink: 0 }} />
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: confidenceColor[verification.verification_confidence], marginBottom: 2 }}>
                  Verification Confidence: {verification.verification_confidence.toUpperCase()}
                </p>
                <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.4 }}>{verification.verification_notes}</p>
              </div>
            </div>

            {/* Verified resource info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ gridColumn: '1 / -1', padding: '12px 14px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8 }}>
                <p style={{ ...labelStyle, marginBottom: 6 }}>Verified Resource Name</p>
                <p style={{ color: '#F5F0E8', fontSize: 13, fontWeight: 600 }}>{verification.verified_name || form.resource_name}</p>
              </div>
              <div style={{ gridColumn: '1 / -1', padding: '12px 14px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8 }}>
                <p style={{ ...labelStyle, marginBottom: 6 }}>Enriched Description</p>
                <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 12, lineHeight: 1.7 }}>{verification.verified_description || form.description}</p>
              </div>
              {verification.verified_address && (
                <div style={{ padding: '10px 12px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 6 }}>
                  <p style={{ ...labelStyle, marginBottom: 4 }}>Address</p>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11 }}>{verification.verified_address}</p>
                </div>
              )}
              {verification.verified_hours && (
                <div style={{ padding: '10px 12px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 6 }}>
                  <p style={{ ...labelStyle, marginBottom: 4 }}>Hours</p>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11 }}>{verification.verified_hours}</p>
                </div>
              )}
              {verification.verified_services && verification.verified_services.length > 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '10px 12px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 6 }}>
                  <p style={{ ...labelStyle, marginBottom: 6 }}>Services Offered</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {verification.verified_services.map((s, i) => (
                      <span key={i} style={{ fontSize: 9, fontFamily: 'monospace', padding: '3px 8px', borderRadius: 3, background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47' }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Name of Allah + Ayat */}
            <div style={{ marginBottom: 16, padding: '16px 18px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 10 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 10 }}>
                ⬡ Name of Allah · Quranic Ayat · MRG Integration
              </p>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ textAlign: 'center', flexShrink: 0, padding: '8px 14px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8 }}>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 26, color: '#c9a84c', direction: 'rtl', marginBottom: 2 }}>{verification.allah_name_ar}</p>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.06em' }}>{verification.allah_name}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.75)', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 6 }}>
                    "{verification.quran_ayat}"
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>{verification.quran_ref}</p>
                  {verification.caretaking_connection && (
                    <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.6, borderTop: '1px solid rgba(201,168,76,0.1)', paddingTop: 8 }}>
                      {verification.caretaking_connection}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 5 }}>
              <p style={{ fontSize: 10, color: 'rgba(201,168,76,0.5)', fontFamily: 'monospace', lineHeight: 1.5 }}>
                ⬡ Amanah Note: This resource will be reviewed by our team before being added to the guide. The Name of Allah and Ayat shown above will be associated with this resource in the guide, inshallah.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setStep('form')} style={{ fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: 5, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(212,197,169,0.5)' }}>
                ← Edit
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  fontSize: 9, fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
                  padding: '8px 22px', borderRadius: 5, cursor: submitting ? 'not-allowed' : 'pointer',
                  background: 'rgba(45,122,71,0.15)', border: '1px solid rgba(45,122,71,0.4)', color: '#2d7a47',
                }}
              >
                {submitting ? 'Submitting...' : '⬡ Submit for Review'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
}
